/**
 * Staff CUPA conflict queue: matched CERS facilities whose scraped CUPA
 * disagrees with the Encamp facility's LEPC-derived CUPA. Persist-on-click
 * flow (per UX): each row shows the two CUPAs as radio-style
 * option cards. Accept CERS saves on click. Keep Encamp is two-beat: clicking
 * selects the card and reveals the required reason input; the decision saves
 * when the input BLURS with a reason, and blurring it empty shows validation
 * instead of saving. Resolved rows stay in place, locked with their
 * outcome, until the page is refreshed. Decisions are one-way for now (undo +
 * a resolved-conflicts view are a follow-up ticket) — hence the warning
 * caption. Deliberately separate from the CERS Facilities matching grid,
 * which stays optimized for quick bulk matching.
 */
import { useMutation, useQuery } from "@apollo/client";
import CheckCircle from "@mui/icons-material/CheckCircle";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Box,
  CircularProgress,
  FormLabel,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColumnVisibilityModel,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  CersCupaConflictResolution,
  StaffCersCupaConflictsQuery,
  StaffCersCupaConflictsQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useOmnisearchState } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid/useOmnisearchDatagrid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { AddressLine } from "../Facilities/MatchCells";
import { RESOLVE_CERS_CUPA_CONFLICT, STAFF_CERS_CUPA_CONFLICTS } from "./api";
import { ConflictRow, encampCupaDisplay } from "./conflictRow";
import { CupaOptionCard } from "./CupaOptionCard";

// ════════════════════════════════════════════════════════════════════════════
// MODULE LEVEL - Static config (stable references, zero runtime overhead)
// ════════════════════════════════════════════════════════════════════════════

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Detail panels open/close with the Keep flow — no user-facing toggle column.
const COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
};

const getItems = (data: StaffCersCupaConflictsQuery) => data.staffCersCupaConflicts.items;
const getCount = (data: StaffCersCupaConflictsQuery) => data.staffCersCupaConflicts.count;
const getRowId = (row: ConflictRow) => row.cersFacility.cersId;

/** "auto" row height so the option cards + reason input expand naturally. */
const getRowHeight = () => "auto" as const;

/**
 * Facility rich cell: linked name + address + county, in the same block format
 * as the matching grid's Encamp cells (MatchCells) — the same staff work both
 * pages, so the layouts must not diverge. Opens in a new tab to preserve the
 * queue context. Unresolved rows carry the "Needs decision" marker; rows
 * resolved this session show their locked outcome until the page refreshes.
 */
function ConflictFacilityCell({
  row,
  resolution,
}: {
  row: ConflictRow;
  resolution: CersCupaConflictResolution | undefined;
}) {
  const { facility } = row;
  return (
    <Box sx={{ py: 0.75 }}>
      <MuiLink
        component={Link}
        to={`/o/${facility.tenantId}/facilities/${facility.id}/profile`}
        target="_blank"
        rel="noopener noreferrer"
        variant="body1"
      >
        {facility.name}
      </MuiLink>
      <AddressLine parts={[facility.streetAddress1, facility.city, facility.state, facility.zip]} />
      {facility.county && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          County: {facility.county}
        </Typography>
      )}
      {resolution === undefined ? (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <WarningAmber sx={{ fontSize: 14, color: "warning.main" }} />
          <Typography variant="caption" color="warning.main">
            Needs decision
          </Typography>
        </Stack>
      ) : (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <CheckCircle sx={{ fontSize: 14, color: "success.main" }} />
          <Typography variant="caption" color="success.main">
            {resolution === CersCupaConflictResolution.AcceptCers
              ? "Resolved — accepted CERS"
              : "Resolved — kept Encamp"}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════════════════

export const CersCupaConflicts = () => {
  // The "CERS" parent crumb comes from the CersTabs layout route; this child
  // adds its own so the trail reads "CERS / CUPA Conflicts".
  const breadcrumbs = useMemo(
    () => [{ label: "CUPA Conflicts", to: "/staff/cers/cupa-conflicts" }],
    []
  );
  useBreadcrumb(breadcrumbs);

  const alerts = useAlerts();

  // Rows resolved this session: outcome by cersFacilityId. Rows stay in the
  // grid, locked, until a refresh (no list refetch on resolve — the matching
  // grid's confirmed-rows-stay-put pattern).
  const [resolvedRows, setResolvedRows] = useState<ReadonlyMap<string, CersCupaConflictResolution>>(
    new Map()
  );
  // Pre-persist keep reasons, by cersFacilityId. Local drafts only — nothing
  // is written until the reason input blurs with a value.
  const [reasonDrafts, setReasonDrafts] = useState<ReadonlyMap<string, string>>(new Map());
  // Rows where Keep Encamp is selected but not yet persisted (reason pending).
  const [pendingKeeps, setPendingKeeps] = useState<ReadonlySet<string>>(new Set());
  // Rows whose reason input was left empty on blur — show validation.
  const [reasonErrors, setReasonErrors] = useState<ReadonlySet<string>>(new Set());
  // Rows with a resolve in flight (their cards disable). A set, not a single
  // id: clicking row B while row A saves must not re-enable A (or B) early.
  const [inFlightIds, setInFlightIds] = useState<ReadonlySet<string>>(new Set());

  // The reason block lives in each row's detail panel (full-width under the
  // option cards, per UX). Panels are controlled: open while a keep is
  // pending, and stay open read-only once a keep is saved.
  const apiRef = useGridApiRef();
  useEffect(() => {
    const lockedKeeps = [...resolvedRows.entries()]
      .filter(([, r]) => r === CersCupaConflictResolution.KeepEncamp)
      .map(([id]) => id);
    apiRef.current?.setExpandedDetailPanels?.(new Set([...pendingKeeps, ...lockedKeeps]));
  }, [apiRef, pendingKeeps, resolvedRows]);

  // Session progress: the companion count is fetched when the page (or the
  // search) loads, so it includes rows resolved later in the session — making
  // remaining = total - resolved. Not refetched on resolve, on purpose.
  const { omnisearch } = useOmnisearchState();
  const searchText = omnisearch.trim();
  const { data: countData } = useQuery(STAFF_CERS_CUPA_CONFLICTS, {
    variables: { search: searchText.length > 0 ? searchText : undefined, page: 0, pageSize: 1 },
    fetchPolicy: "cache-and-network",
  });
  const totalCount = countData?.staffCersCupaConflicts.count;

  const [resolveConflict] = useMutation(RESOLVE_CERS_CUPA_CONFLICT);

  const setRowReason = useCallback((cersFacilityId: string, reason: string) => {
    setReasonDrafts((prev) => new Map(prev).set(cersFacilityId, reason));
    // Typing clears the left-it-empty validation state.
    setReasonErrors((prev) => {
      if (!prev.has(cersFacilityId)) return prev;
      const next = new Set(prev);
      next.delete(cersFacilityId);
      return next;
    });
  }, []);

  const toggleKeepPending = useCallback((cersFacilityId: string) => {
    setPendingKeeps((prev) => {
      const next = new Set(prev);
      if (next.has(cersFacilityId)) {
        next.delete(cersFacilityId);
      } else {
        next.add(cersFacilityId);
      }
      return next;
    });
    setReasonErrors((prev) => {
      if (!prev.has(cersFacilityId)) return prev;
      const next = new Set(prev);
      next.delete(cersFacilityId);
      return next;
    });
  }, []);

  const handleResolve = useCallback(
    (row: ConflictRow, resolution: CersCupaConflictResolution, note?: string) => {
      const cersFacilityId = row.cersFacility.cersId;
      setInFlightIds((prev) => new Set(prev).add(cersFacilityId));
      void resolveConflict({
        variables: {
          cersFacilityId,
          resolution,
          note,
          // Guard: reject if the Encamp side changed since this row rendered.
          expectedEncampLepcId: row.encampLepc.id,
        },
      })
        .then(() => {
          setResolvedRows((prev) => new Map(prev).set(cersFacilityId, resolution));
          setPendingKeeps((prev) => {
            if (!prev.has(cersFacilityId)) return prev;
            const next = new Set(prev);
            next.delete(cersFacilityId);
            return next;
          });
        })
        .catch((e: unknown) => {
          alerts.error(
            `Couldn't resolve ${row.facility.name}: ${e instanceof Error ? e.message : String(e)}`
          );
        })
        .finally(() =>
          setInFlightIds((prev) => {
            const next = new Set(prev);
            next.delete(cersFacilityId);
            return next;
          })
        );
    },
    [resolveConflict, alerts]
  );

  // Columns capture the per-row session state; memoized on it to keep
  // OmnisearchDataGridV2's stable-props check happy.
  const columns = useMemo<OmnisearchGridColDef<ConflictRow>[]>(
    () => [
      {
        field: "facility",
        headerName: "Facility",
        flex: 1.1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <ConflictFacilityCell row={row} resolution={resolvedRows.get(row.cersFacility.cersId)} />
        ),
      },
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 0.6,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.facility.tenant?.name ?? "",
      },
      {
        field: "cersCupa",
        headerName: "CERS CUPA",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const id = row.cersFacility.cersId;
          const resolution = resolvedRows.get(id);
          return (
            <CupaOptionCard
              label={`${row.cersCupa.displayName} (${row.cersCupa.cupaCode})`}
              selected={resolution === CersCupaConflictResolution.AcceptCers}
              disabled={resolution !== undefined || inFlightIds.has(id)}
              onSelect={() => handleResolve(row, CersCupaConflictResolution.AcceptCers)}
            />
          );
        },
      },
      {
        field: "encampCupa",
        headerName: "Encamp CUPA (LEPC)",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const id = row.cersFacility.cersId;
          const resolution = resolvedRows.get(id);
          return (
            <CupaOptionCard
              label={encampCupaDisplay(row)}
              selected={
                resolution === CersCupaConflictResolution.KeepEncamp || pendingKeeps.has(id)
              }
              disabled={resolution !== undefined || inFlightIds.has(id)}
              onSelect={() => toggleKeepPending(id)}
            />
          );
        },
      },
    ],
    [resolvedRows, pendingKeeps, inFlightIds, handleResolve, toggleKeepPending]
  );

  // Full-width reason block under the option cards (per UX mock): editable
  // while the keep is pending (persists on blur, validates on empty blur),
  // read-only once saved.
  const renderReasonPanel = useCallback(
    (params: GridRowParams<ConflictRow>) => {
      const row = params.row;
      const id = row.cersFacility.cersId;
      const resolution = resolvedRows.get(id);
      const reason = reasonDrafts.get(id) ?? "";
      // Per the UX mock the reason block sits under the two CUPA columns, not
      // full width — indent to the CERS column's live left edge.
      const cupaColumnsLeft = apiRef.current?.getColumnPosition?.("cersCupa") ?? 0;
      if (resolution === CersCupaConflictResolution.KeepEncamp) {
        return (
          <Paper elevation={0} square sx={{ pl: `${cupaColumnsLeft + 10}px`, pr: 3, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Reason for keeping Encamp: {reason.trim() || "—"}
            </Typography>
          </Paper>
        );
      }
      if (!pendingKeeps.has(id) || resolution !== undefined) return null;
      const showError = reasonErrors.has(id);
      return (
        <Paper elevation={0} square sx={{ pl: `${cupaColumnsLeft + 10}px`, pr: 3, py: 2 }}>
          <FormLabel required sx={{ display: "block", mb: 1, fontSize: "0.875rem" }}>
            Reason for keeping Encamp
          </FormLabel>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={reason}
            error={showError}
            onChange={(e) => setRowReason(id, e.target.value)}
            // Persist-on-blur (per UX): leaving the field with a reason saves
            // the decision; leaving it empty shows validation instead.
            onBlur={() => {
              const trimmed = reason.trim();
              if (trimmed.length > 0) {
                handleResolve(row, CersCupaConflictResolution.KeepEncamp, trimmed);
              } else {
                setReasonErrors((prev) => new Set(prev).add(id));
              }
            }}
          />
          <Typography
            variant="caption"
            color={showError ? "error.main" : "text.secondary"}
            sx={{ display: "block", mt: 0.5 }}
          >
            {showError
              ? "Reason is required to keep the Encamp value."
              : "Required. Saved with the decision — visible when this override is reviewed later."}
          </Typography>
        </Paper>
      );
    },
    [resolvedRows, pendingKeeps, reasonDrafts, reasonErrors, handleResolve, setRowReason, apiRef]
  );

  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  const buildVariables = useCallback(
    (
      currentOmnisearch: string,
      pagination: { page: number; pageSize: number },
      _sort: GridSortModel
    ): StaffCersCupaConflictsQueryVariables => {
      const text = currentOmnisearch.trim();
      return {
        search: text.length > 0 ? text : undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
      };
    },
    []
  );

  const resolvedCount = resolvedRows.size;
  const remaining = totalCount === undefined ? undefined : Math.max(totalCount - resolvedCount, 0);
  const progress =
    totalCount === undefined || totalCount === 0
      ? 0
      : Math.min((resolvedCount / totalCount) * 100, 100);

  return (
    // Layout matches the Rejections tab: no horizontal padding on the page
    // stack — header content gets px:3 to line up with the grid's internal
    // px:3, so every left edge sits at the same 24px.
    <Stack sx={{ pt: 3 }} spacing={2}>
      <Stack spacing={2} sx={{ px: 3 }}>
        <Typography variant="h5">CERS CUPA Conflicts</Typography>
        <Typography variant="body2" color="text.secondary">
          Matched facilities where the CUPA that reviewed the CERS submittals disagrees with the
          facility&apos;s CUPA in Encamp. Accepting CERS updates the facility; keeping Encamp
          (reason required) suppresses the conflict until either side changes.
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <WarningAmber sx={{ fontSize: 16, color: "warning.main" }} />
          <Typography variant="body2" color="warning.main">
            Decisions save immediately when you click and can&apos;t be undone yet — double-check
            before choosing.
          </Typography>
        </Stack>
        {totalCount !== undefined && totalCount > 0 && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">
                <b>{resolvedCount}</b> of {totalCount} resolved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {remaining} still open
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
          </Box>
        )}
        {inFlightIds.size > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Saving…
            </Typography>
          </Stack>
        )}
      </Stack>
      <OmnisearchDataGridV2
        query={STAFF_CERS_CUPA_CONFLICTS}
        getItems={getItems}
        getCount={getCount}
        getRowId={getRowId}
        persistenceKey="staff-cers-cupa-conflicts-table"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        columns={columns}
        buildVariables={buildVariables}
        getRowHeight={getRowHeight}
        initialColumnVisibilityModel={COLUMN_VISIBILITY}
        apiRef={apiRef}
        getDetailPanelContent={renderReasonPanel}
        getDetailPanelHeight={getDetailPanelHeight}
        noRowsMessage="No CUPA conflicts — every matched facility agrees with CERS."
      />
    </Stack>
  );
};
